/**
 * OptimizedRouteCard - Card de rota otimizada
 * Exibe resumo da rota e lista de paradas usando DeliveryTimeline
 */

import { memo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Divider,
  LinearProgress,
  Button,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Route as RouteIcon,
  AccessTime as TimeIcon,
  Straighten as DistanceIcon,
  LocalShipping as VehicleIcon,
  PersonAdd as AssignIcon,
  Person as DriverIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { formatDistance, formatDuration } from '../../common/services/routingService';
import DraggableDeliveryTimeline from './DraggableDeliveryTimeline';
import { STOP_STATUS } from './DeliveryTimeline';

const ROUTE_COLORS = [
  '#00d4aa', // teal
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#10b981', // emerald
  '#3b82f6', // blue
  '#ec4899', // pink
  '#6366f1', // indigo
];

const OptimizedRouteCard = memo(({
  route,
  vehicle,
  index,
  expanded: controlledExpanded,
  onExpandChange,
  onStepClick,
  onOrderChange,
  activeStepId,
  allowReorder = true,
  onAssignDriver,
  planId,
  planVehicleId,
}) => {
  const tokens = usePremiumTokens();
  const [internalExpanded, setInternalExpanded] = useState(false);

  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const handleExpand = () => {
    if (onExpandChange) {
      onExpandChange(!expanded);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const routeColor = ROUTE_COLORS[index % ROUTE_COLORS.length];
  const jobSteps = route.steps?.filter((s) => s.type === 'job') || [];
  const completedSteps = jobSteps.filter((s) => s.completed || s.status === STOP_STATUS.COMPLETED).length;
  const progress = jobSteps.length > 0 ? (completedSteps / jobSteps.length) * 100 : 0;

  return (
    <Box
      sx={{
        backgroundColor: tokens?.surface?.raised,
        borderRadius: tokens?.radius?.lg || '12px',
        border: `1px solid ${tokens?.border?.subtle}`,
        overflow: 'hidden',
        transition: tokens?.transitions?.all,
      }}
    >
      {/* Header */}
      <Box
        onClick={handleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: tokens?.surface?.hover,
          },
        }}
      >
        {/* Indicador de cor */}
        <Box
          sx={{
            width: 4,
            height: 40,
            borderRadius: 2,
            backgroundColor: routeColor,
            flexShrink: 0,
          }}
        />

        {/* Icone do veiculo */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: tokens?.radius?.md,
            backgroundColor: `${routeColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <VehicleIcon sx={{ color: routeColor, fontSize: 20 }} />
        </Box>

        {/* Informacoes */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {vehicle?.name || `Veiculo ${route.vehicleId}`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RouteIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                {jobSteps.length} paradas
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DistanceIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                {formatDistance(route.distance)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
              <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
                {formatDuration(route.duration)}
              </Typography>
            </Box>
          </Box>

          {/* Driver info */}
          {vehicle?.driverName && (
            <Chip
              icon={<DriverIcon sx={{ fontSize: '14px !important' }} />}
              label={vehicle.driverName}
              size="small"
              sx={{
                mt: 0.5,
                height: 22,
                fontSize: '11px',
                backgroundColor: `${tokens?.accent?.primary}15`,
                color: tokens?.accent?.primary,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}

          {/* Progress bar */}
          {completedSteps > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: tokens?.surface?.elevated,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: routeColor,
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Assign driver button */}
        {onAssignDriver && !vehicle?.driverName && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AssignIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onAssignDriver({ planId, planVehicleId, vehicle });
            }}
            sx={{
              mr: 1,
              fontSize: '11px',
              textTransform: 'none',
              borderColor: tokens?.accent?.primary,
              color: tokens?.accent?.primary,
              '&:hover': {
                borderColor: tokens?.accent?.hover,
                backgroundColor: `${tokens?.accent?.primary}10`,
              },
            }}
          >
            Atribuir
          </Button>
        )}

        {/* Expand button */}
        <IconButton
          size="small"
          sx={{
            color: tokens?.text?.tertiary,
          }}
        >
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      {/* Steps expandidos com DraggableDeliveryTimeline */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: tokens?.border?.subtle }} />

        <Box sx={{ p: 2 }}>
          <DraggableDeliveryTimeline
            steps={route.steps || []}
            routeColor={routeColor}
            activeStepId={activeStepId}
            onStepClick={onStepClick}
            onOrderChange={onOrderChange}
            showConnector
            totalDistance={route.distance}
            totalDuration={route.duration}
            disabled={!allowReorder}
          />
        </Box>
      </Collapse>
    </Box>
  );
});

OptimizedRouteCard.displayName = 'OptimizedRouteCard';

export default OptimizedRouteCard;
